import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { 
  ArrowLeft, Phone, Mail, Calendar, MapPin, Loader2, Plus, 
  Edit2, Trash2, Save, X, MessageSquare, Clock
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import axios from 'axios';
import ClientAlertBanner from '../components/community/ClientAlertBanner';
import AddAppointmentDialog from '../components/AddAppointmentDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ANIMAL_TYPES = [
  { value: 'HORSE',   label: 'Horse' },
  { value: 'COW',     label: 'Cow' },
  { value: 'SHEEP',   label: 'Sheep' },
  { value: 'GOAT',    label: 'Goat' },
  { value: 'PIG',     label: 'Pig' },
  { value: 'CHICKEN', label: 'Chicken' },
  { value: 'DOG',     label: 'Dog' },
  { value: 'CAMEL',   label: 'Camel' },
  { value: 'EXOTIC',  label: 'Exotic' },
  { value: 'OTHER',   label: 'Other' },
];

export default function ClientDetailPage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [client, setClient] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Animal form state
  const [showHorseDialog, setShowHorseDialog] = useState(false);
  const [editingHorse, setEditingHorse] = useState(null);
  const [horseForm, setHorseForm] = useState({
    name: '',
    animalType: 'HORSE',
    breed: '',
    age: '',
    notes: '',
    handlingNotes: '',
    behaviorNotes: '',
  });
  
  // Note form state
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [noteText, setNoteText] = useState('');

  useEffect(() => {
    loadClientDetails();
  }, [clientId]);

  const loadClientDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/dashboard/clients/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClient(response.data);
    } catch (error) {
      console.error('Error loading client:', error);
      toast.error('Failed to load client details');
      navigate('/dashboard/clients');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveHorse = async () => {
    if (!horseForm.name.trim()) {
      toast.error('Animal name is required');
      return;
    }
    
    try {
      setSaving(true);
      
      const horseData = {
        clientId,
        name: horseForm.name,
        animalType: horseForm.animalType || 'OTHER',
        breed: horseForm.breed || null,
        age: horseForm.age ? parseInt(horseForm.age) : null,
        notes: horseForm.notes || null,
        handlingNotes: horseForm.handlingNotes || null,
        behaviorNotes: horseForm.behaviorNotes || null,
      };
      
      if (editingHorse) {
        await axios.patch(
          `${API_URL}/api/dashboard/horses/${editingHorse.id}`,
          horseData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Animal updated');
      } else {
        await axios.post(
          `${API_URL}/api/dashboard/clients/${clientId}/horses`,
          horseData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Animal added');
      }
      
      setShowHorseDialog(false);
      setEditingHorse(null);
      setHorseForm({ name: '', animalType: 'HORSE', breed: '', age: '', notes: '', handlingNotes: '', behaviorNotes: '' });
      loadClientDetails();
    } catch (error) {
      console.error('Error saving horse:', error);
      toast.error('Failed to save animal');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteHorse = async (horseId) => {
    if (!confirm('Are you sure you want to delete this animal record?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/dashboard/horses/${horseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Animal deleted');
      loadClientDetails();
    } catch (error) {
      console.error('Error deleting horse:', error);
      toast.error('Failed to delete animal');
    }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) {
      toast.error('Note cannot be empty');
      return;
    }
    
    try {
      setSaving(true);
      await axios.post(
        `${API_URL}/api/dashboard/clients/${clientId}/notes`,
        { note: noteText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Note added');
      setShowNoteDialog(false);
      setNoteText('');
      loadClientDetails();
    } catch (error) {
      console.error('Error adding note:', error);
      toast.error('Failed to add note');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!confirm('Are you sure you want to delete this note?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/dashboard/clients/notes/${noteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Note deleted');
      loadClientDetails();
    } catch (error) {
      console.error('Error deleting note:', error);
      toast.error('Failed to delete note');
    }
  };

  const openEditHorse = (horse) => {
    setEditingHorse(horse);
    setHorseForm({
      name: horse.name,
      animalType: horse.animalType || 'HORSE',
      breed: horse.breed || '',
      age: horse.age?.toString() || '',
      notes: horse.notes || '',
      handlingNotes: horse.handlingNotes || '',
      behaviorNotes: horse.behaviorNotes || '',
    });
    setShowHorseDialog(true);
  };

  const getStatusBadge = (status) => {
    const variants = {
      PENDING: 'secondary',
      CONFIRMED: 'default',
      COMPLETED: 'outline',
      CANCELED: 'destructive'
    };
    return <Badge variant={variants[status] || 'secondary'}>{status}</Badge>;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  if (!client) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">Client not found</p>
          <Button onClick={() => navigate('/dashboard/clients')} className="mt-4">
            Back to Clients
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard/clients')}
            className="mb-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Clients
          </Button>
          
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
            <div>
              <h1 className="text-2xl font-bold" data-testid="client-name">
                {client.name}
              </h1>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Phone className="h-4 w-4" />
                  <a href={`tel:${client.phone}`} className="hover:underline">{client.phone}</a>
                </div>
                {client.email && (
                  <div className="flex items-center gap-1">
                    <Mail className="h-4 w-4" />
                    <a href={`mailto:${client.email}`} className="hover:underline">{client.email}</a>
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <Badge variant="outline" className="py-1 px-3">
                {client.totalBookings} booking{client.totalBookings !== 1 ? 's' : ''}
              </Badge>
              <Badge variant="outline" className="py-1 px-3">
                {client.horses?.length || 0} animal{(client.horses?.length || 0) !== 1 ? 's' : ''}
              </Badge>
              <AddAppointmentDialog onCreated={loadClientDetails} />
            </div>
          </div>
        </div>

        {/* Provider Alert Banner - warns provider if client has community alerts */}
        <ClientAlertBanner
          clientName={client.name}
          clientEmail={client.email}
          clientPhone={client.phone}
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Animals Section */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    Animals
                  </CardTitle>
                  <CardDescription>
                    Manage animal records for this client
                  </CardDescription>
                </div>
                <Dialog open={showHorseDialog} onOpenChange={(open) => {
                  setShowHorseDialog(open);
                  if (!open) {
                    setEditingHorse(null);
                    setHorseForm({ name: '', animalType: 'HORSE', breed: '', age: '', notes: '', handlingNotes: '', behaviorNotes: '' });
                  }
                }}>
                  <DialogTrigger asChild>
                    <Button size="sm" data-testid="add-horse-button">
                      <Plus className="mr-2 h-4 w-4" />
                      Add Animal
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>
                        {editingHorse ? 'Edit Animal' : 'Add Animal'}
                      </DialogTitle>
                      <DialogDescription>
                        {editingHorse ? 'Update animal information' : 'Add a new animal for this client'}
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="horseName">Name *</Label>
                          <Input
                            id="horseName"
                            value={horseForm.name}
                            onChange={(e) => setHorseForm({ ...horseForm, name: e.target.value })}
                            placeholder="Animal name"
                            data-testid="horse-name-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="animalType">Type *</Label>
                          <Select
                            value={horseForm.animalType}
                            onValueChange={(v) => setHorseForm({ ...horseForm, animalType: v })}
                          >
                            <SelectTrigger id="animalType" data-testid="animal-type-select">
                              <SelectValue placeholder="Pick type" />
                            </SelectTrigger>
                            <SelectContent>
                              {ANIMAL_TYPES.map((t) => (
                                <SelectItem key={t.value} value={t.value} data-testid={`animal-type-${t.value.toLowerCase()}`}>
                                  {t.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="horseBreed">Breed</Label>
                          <Input
                            id="horseBreed"
                            value={horseForm.breed}
                            onChange={(e) => setHorseForm({ ...horseForm, breed: e.target.value })}
                            placeholder="e.g., Quarter Horse, Hereford"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="horseAge">Age (years)</Label>
                          <Input
                            id="horseAge"
                            type="number"
                            min="0"
                            max="50"
                            value={horseForm.age}
                            onChange={(e) => setHorseForm({ ...horseForm, age: e.target.value })}
                            placeholder="e.g., 8"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="animalNotes">Notes</Label>
                        <Textarea
                          id="animalNotes"
                          value={horseForm.notes}
                          onChange={(e) => setHorseForm({ ...horseForm, notes: e.target.value })}
                          placeholder="Any special notes about this animal..."
                          rows={2}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="handlingNotes">Handling notes</Label>
                        <Textarea
                          id="handlingNotes"
                          value={horseForm.handlingNotes}
                          onChange={(e) => setHorseForm({ ...horseForm, handlingNotes: e.target.value })}
                          placeholder="How to safely catch / lead / restrain..."
                          rows={2}
                          data-testid="animal-handling-notes"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="behaviorNotes">Behavior notes</Label>
                        <Textarea
                          id="behaviorNotes"
                          value={horseForm.behaviorNotes}
                          onChange={(e) => setHorseForm({ ...horseForm, behaviorNotes: e.target.value })}
                          placeholder="Kicks, bites, herd boss, easily spooked..."
                          rows={2}
                          data-testid="animal-behavior-notes"
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowHorseDialog(false)}>
                        Cancel
                      </Button>
                      <Button onClick={handleSaveHorse} disabled={saving} data-testid="save-animal-btn">
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                          <>
                            <Save className="mr-2 h-4 w-4" />
                            {editingHorse ? 'Save Changes' : 'Add Animal'}
                          </>
                        )}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {client.horses?.length === 0 ? (
                  <p className="text-center text-muted-foreground py-6">
                    No animals registered for this client
                  </p>
                ) : (
                  <div className="grid gap-3" data-testid="horses-list">
                    {client.horses?.map((horse) => {
                      const typeLabel = ANIMAL_TYPES.find(t => t.value === horse.animalType)?.label || 'Horse';
                      return (
                      <div
                        key={horse.id}
                        className="flex items-start justify-between p-3 bg-muted rounded-lg"
                      >
                        <div>
                          <div className="font-semibold flex items-center">
                            {horse.name}
                            <Badge variant="secondary" className="ml-2 text-xs align-middle">
                              {typeLabel}
                            </Badge>
                          </div>
                          <div className="flex gap-3 text-sm text-muted-foreground">
                            {horse.breed && <span>{horse.breed}</span>}
                            {horse.age && <span>{horse.age} years old</span>}
                          </div>
                          {horse.notes && (
                            <p className="text-sm text-muted-foreground mt-1">{horse.notes}</p>
                          )}
                          {horse.handlingNotes && (
                            <p className="text-xs mt-1"><span className="font-medium">Handling: </span>{horse.handlingNotes}</p>
                          )}
                          {horse.behaviorNotes && (
                            <p className="text-xs mt-1"><span className="font-medium">Behavior: </span>{horse.behaviorNotes}</p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditHorse(horse)}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteHorse(horse.id)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Appointment History */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Appointment History
                </CardTitle>
              </CardHeader>
              <CardContent>
                {client.bookings?.length === 0 ? (
                  <p className="text-center text-muted-foreground py-6">
                    No appointments yet
                  </p>
                ) : (
                  <div className="space-y-3" data-testid="bookings-list">
                    {client.bookings?.map((booking) => (
                      <div
                        key={booking.id}
                        className="flex items-center justify-between p-3 border rounded-lg"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-semibold">{booking.serviceName}</p>
                            {getStatusBadge(booking.status)}
                          </div>
                          <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {format(new Date(booking.startDateTime), 'MMM d, yyyy')}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {format(new Date(booking.startDateTime), 'h:mm a')}
                            </span>
                            <span>{booking.horseCount} animal{booking.horseCount !== 1 ? 's' : ''}</span>
                            {typeof booking.totalCents === 'number' && booking.totalCents > 0 && (
                              <span className="font-medium text-foreground">${(booking.totalCents / 100).toFixed(2)}</span>
                            )}
                            {booking.paymentStatus && (
                              <Badge
                                variant={booking.paymentStatus === 'PAID' ? 'default' : booking.paymentStatus === 'PARTIAL' ? 'secondary' : 'destructive'}
                                className="text-[10px]"
                              >
                                {booking.paymentStatus}
                              </Badge>
                            )}
                          </div>
                          {Array.isArray(booking.appointmentItems) && booking.appointmentItems.length > 0 && (
                            <ul className="mt-2 text-sm space-y-0.5" data-testid={`booking-items-${booking.id}`}>
                              {booking.appointmentItems.map((it) => (
                                <li key={it.itemId} className="flex justify-between gap-2">
                                  <span className="text-muted-foreground">
                                    <span className="font-medium text-foreground">{it.animalName || 'Unlabeled'}</span>
                                    {' — '}{it.serviceName || 'Service'}
                                  </span>
                                  {typeof it.priceCents === 'number' && (
                                    <span className="tabular-nums">${(it.priceCents/100).toFixed(2)}</span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                          {booking.locationAddress && (
                            <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {booking.locationAddress}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar - Notes */}
          <div className="space-y-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="h-5 w-5" />
                    Notes
                  </CardTitle>
                  <CardDescription>
                    Internal notes about this client
                  </CardDescription>
                </div>
                <Dialog open={showNoteDialog} onOpenChange={setShowNoteDialog}>
                  <DialogTrigger asChild>
                    <Button size="sm" variant="outline" data-testid="add-note-button">
                      <Plus className="mr-2 h-4 w-4" />
                      Add
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add Note</DialogTitle>
                      <DialogDescription>
                        Add an internal note about this client
                      </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                      <Textarea
                        value={noteText}
                        onChange={(e) => setNoteText(e.target.value)}
                        placeholder="Enter your note..."
                        rows={4}
                        data-testid="note-input"
                      />
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowNoteDialog(false)}>
                        Cancel
                      </Button>
                      <Button onClick={handleAddNote} disabled={saving}>
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Note'}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {client.notes?.length === 0 ? (
                  <p className="text-center text-muted-foreground py-6 text-sm">
                    No notes yet
                  </p>
                ) : (
                  <div className="space-y-3" data-testid="notes-list">
                    {client.notes?.map((note) => (
                      <div
                        key={note.id}
                        className="p-3 bg-muted rounded-lg"
                      >
                        <div className="flex justify-between items-start">
                          <p className="text-sm whitespace-pre-wrap">{note.note}</p>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteNote(note.id)}
                            className="flex-shrink-0 ml-2"
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">
                          {format(new Date(note.createdAt), 'MMM d, yyyy h:mm a')}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Client Info Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Client Since</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <p className="text-lg font-semibold">
                  {client.firstAppointment 
                    ? format(new Date(client.firstAppointment), 'MMMM yyyy')
                    : 'N/A'
                  }
                </p>
                {client.lastAppointment && (
                  <p className="text-sm text-muted-foreground mt-2">
                    Last visit: {format(new Date(client.lastAppointment), 'MMM d, yyyy')}
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
